/* eslint no-underscore-dangle: "off" */
/* eslint no-use-before-define: "off" */
import { getDocument } from 'ssr-window';
import { nextTick } from '../../shared/utils.js';

export default function Autoplay({ swiper, extendParams, on, emit }) {
  let timeout;

  swiper.Autoplay = {
    running: false,
    paused: false,
  };

  extendParams({
    Autoplay: {
      enabled: false,
      delay: 3000,
      waitForTransition: true,
      disableOnInteraction: true,
      stopOnLastSlide: false,
      reverseDirection: false,
      pauseOnMouseEnter: false,
    },
  });

  function run() {
    if (!swiper.size) {
      swiper.Autoplay.running = false;
      swiper.Autoplay.paused = false;
      return;
    }
    const $activeSlideEl = swiper.slides.eq(swiper.activeIndex);
    let delay = swiper.params.Autoplay.delay;
    if ($activeSlideEl.attr('data-swiper-Autoplay')) {
      delay = $activeSlideEl.attr('data-swiper-Autoplay') || swiper.params.Autoplay.delay;
    }
    clearTimeout(timeout);
    timeout = nextTick(() => {
      let AutoplayResult;
      if (swiper.params.Autoplay.reverseDirection) {
        if (swiper.params.loop) {
          swiper.loopFix();
          AutoplayResult = swiper.slidePrev(swiper.params.speed, true, true);
          emit('Autoplay');
        } else if (!swiper.isBeginning) {
          AutoplayResult = swiper.slidePrev(swiper.params.speed, true, true);
          emit('Autoplay');
        } else if (!swiper.params.Autoplay.stopOnLastSlide) {
          AutoplayResult = swiper.slideTo(
            swiper.slides.length - 1,
            swiper.params.speed,
            true,
            true,
          );
          emit('Autoplay');
        } else {
          stop();
        }
      } else if (swiper.params.loop) {
        swiper.loopFix();
        AutoplayResult = swiper.slideNext(swiper.params.speed, true, true);
        emit('Autoplay');
      } else if (!swiper.isEnd) {
        AutoplayResult = swiper.slideNext(swiper.params.speed, true, true);
        emit('Autoplay');
      } else if (!swiper.params.Autoplay.stopOnLastSlide) {
        AutoplayResult = swiper.slideTo(0, swiper.params.speed, true, true);
        emit('Autoplay');
      } else {
        stop();
      }
      if (swiper.params.cssMode && swiper.Autoplay.running) run();
      else if (AutoplayResult === false) {
        run();
      }
    }, delay);
  }
  function start() {
    if (typeof timeout !== 'undefined') return false;
    if (swiper.Autoplay.running) return false;
    swiper.Autoplay.running = true;
    emit('AutoplayStart');
    run();
    return true;
  }
  function stop() {
    if (!swiper.Autoplay.running) return false;
    if (typeof timeout === 'undefined') return false;

    if (timeout) {
      clearTimeout(timeout);
      timeout = undefined;
    }
    swiper.Autoplay.running = false;
    emit('AutoplayStop');
    return true;
  }
  function pause(speed) {
    if (!swiper.Autoplay.running) return;
    if (swiper.Autoplay.paused) return;
    if (timeout) clearTimeout(timeout);
    swiper.Autoplay.paused = true;
    if (speed === 0 || !swiper.params.Autoplay.waitForTransition) {
      swiper.Autoplay.paused = false;
      run();
    } else {
      ['transitionend', 'webkitTransitionEnd'].forEach((event) => {
        swiper.$wrapperEl[0].addEventListener(event, onTransitionEnd);
      });
    }
  }
  function onVisibilityChange() {
    const document = getDocument();
    if (document.visibilityState === 'hidden' && swiper.Autoplay.running) {
      pause();
    }
    if (document.visibilityState === 'visible' && swiper.Autoplay.paused) {
      run();
      swiper.Autoplay.paused = false;
    }
  }
  function onTransitionEnd(e) {
    if (!swiper || swiper.destroyed || !swiper.$wrapperEl) return;
    if (e.target !== swiper.$wrapperEl[0]) return;
    ['transitionend', 'webkitTransitionEnd'].forEach((event) => {
      swiper.$wrapperEl[0].removeEventListener(event, onTransitionEnd);
    });
    swiper.Autoplay.paused = false;
    if (!swiper.Autoplay.running) {
      stop();
    } else {
      run();
    }
  }
  function onMouseEnter() {
    if (swiper.params.Autoplay.disableOnInteraction) {
      stop();
    } else {
      emit('AutoplayPause');
      pause();
    }

    ['transitionend', 'webkitTransitionEnd'].forEach((event) => {
      swiper.$wrapperEl[0].removeEventListener(event, onTransitionEnd);
    });
  }
  function onMouseLeave() {
    if (swiper.params.Autoplay.disableOnInteraction) {
      return;
    }
    swiper.Autoplay.paused = false;
    emit('AutoplayResume');
    run();
  }
  function attachMouseEvents() {
    if (swiper.params.Autoplay.pauseOnMouseEnter) {
      swiper.$el.on('mouseenter', onMouseEnter);
      swiper.$el.on('mouseleave', onMouseLeave);
    }
  }
  function detachMouseEvents() {
    swiper.$el.off('mouseenter', onMouseEnter);
    swiper.$el.off('mouseleave', onMouseLeave);
  }

  on('init', () => {
    if (swiper.params.Autoplay.enabled) {
      start();
      const document = getDocument();
      document.addEventListener('visibilitychange', onVisibilityChange);
      attachMouseEvents();
    }
  });
  on('beforeTransitionStart', (_s, speed, internal) => {
    if (swiper.Autoplay.running) {
      if (internal || !swiper.params.Autoplay.disableOnInteraction) {
        swiper.Autoplay.pause(speed);
      } else {
        stop();
      }
    }
  });
  on('sliderFirstMove', () => {
    if (swiper.Autoplay.running) {
      if (swiper.params.Autoplay.disableOnInteraction) {
        stop();
      } else {
        pause();
      }
    }
  });
  on('touchEnd', () => {
    if (
      swiper.params.cssMode &&
      swiper.Autoplay.paused &&
      !swiper.params.Autoplay.disableOnInteraction
    ) {
      run();
    }
  });
  on('destroy', () => {
    detachMouseEvents();
    if (swiper.Autoplay.running) {
      stop();
    }
    const document = getDocument();
    document.removeEventListener('visibilitychange', onVisibilityChange);
  });

  Object.assign(swiper.Autoplay, {
    pause,
    run,
    start,
    stop,
  });
}
